import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Wrench, LogIn, MessageCircle, BellRing, CheckCircle2, Loader2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Maintenance({ message }) {
    return (
        <div className="relative min-h-screen flex items-center justify-center overflow-hidden" data-testid="maintenance-page">
            <img src="/maintenance-visual.jpg" alt="African Valley"
                className="absolute inset-0 h-full w-full object-cover" draggable={false} />
            <div className="absolute inset-0 bg-black/55" />
            <div className="relative z-10 max-w-2xl mx-auto px-6 text-center text-white">
                <div className="mx-auto h-14 w-14 rounded-full bg-white/10 backdrop-blur border border-white/20 flex items-center justify-center mb-6">
                    <Wrench size={24} strokeWidth={1.6} className="text-amber-300" />
                </div>
                <div className="text-[11px] uppercase tracking-[0.3em] text-amber-300 mb-4">African Valley · Projets 360°</div>
                <h1 className="font-heading text-4xl sm:text-5xl leading-tight">Site en maintenance</h1>
                <p className="mt-5 text-white/85 leading-relaxed text-base sm:text-lg" data-testid="maintenance-message">
                    {message || "Nous améliorons actuellement la plateforme pour vous offrir une meilleure expérience. Le site public sera de retour très prochainement."}
                </p>
                <p className="mt-3 text-white/60 text-sm">
                    Les utilisateurs disposant d'un compte actif peuvent continuer à se connecter normalement.
                </p>
                <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                    <Link to="/auth/signin" data-testid="maintenance-signin-btn"
                        className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-primary hover:bg-primary-hover text-white font-medium transition-colors">
                        <LogIn size={16} strokeWidth={1.8} /> Se connecter
                    </Link>
                    <Link to="/p/contact" data-testid="maintenance-contact-btn"
                        className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur border border-white/30 text-white font-medium transition-colors">
                        <MessageCircle size={16} strokeWidth={1.8} /> Parler à un expert
                    </Link>
                </div>
                <NotifyMeForm />
            </div>
        </div>
    );
}

// Mini-formulaire « Être prévenu du retour » — capture d'email vers le CRM (source=maintenance).
function NotifyMeForm() {
    const [email, setEmail] = useState('');
    const [website, setWebsite] = useState(''); // honeypot
    const [busy, setBusy] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState(null);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true); setError(null);
        try {
            await axios.post(`${API}/tech/maintenance/subscribe`, { email, website });
            setSent(true);
        } catch (err) {
            setError(err.response?.data?.detail || 'Une erreur est survenue, réessayez.');
        }
        setBusy(false);
    };

    if (sent) {
        return (
            <div className="mt-10 mx-auto max-w-md rounded-2xl bg-white/10 backdrop-blur border border-white/20 px-6 py-5 flex items-center gap-3 text-left"
                data-testid="maintenance-notify-success">
                <CheckCircle2 size={22} className="text-emerald-300 shrink-0" />
                <p className="text-sm text-white/90">
                    C'est noté ! Vous recevrez un email dès le retour du site.
                </p>
            </div>
        );
    }

    return (
        <form onSubmit={submit} className="mt-10 mx-auto max-w-md text-left" data-testid="maintenance-notify-form">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-white/70 mb-2">
                <BellRing size={13} className="text-amber-300" /> Être prévenu du retour
            </div>
            <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', height: 0, overflow: 'hidden' }}>
                <label>Site web<input type="text" tabIndex={-1} autoComplete="off" value={website}
                    onChange={(e) => setWebsite(e.target.value)} /></label>
            </div>
            <div className="flex gap-2">
                <input type="email" required value={email} placeholder="votre@email.com"
                    onChange={(e) => setEmail(e.target.value)}
                    className="flex-1 h-12 px-4 rounded-full bg-white/10 backdrop-blur border border-white/25 text-white placeholder:text-white/45 text-sm focus:outline-none focus:border-amber-300/70"
                    data-testid="maintenance-notify-email" />
                <button type="submit" disabled={busy}
                    className="h-12 px-6 rounded-full bg-amber-400 hover:bg-amber-300 text-stone-900 text-sm font-semibold transition-colors disabled:opacity-60 inline-flex items-center gap-2"
                    data-testid="maintenance-notify-submit">
                    {busy ? <Loader2 size={15} className="animate-spin" /> : 'Me prévenir'}
                </button>
            </div>
            {error && <p className="mt-2 text-xs text-red-300" data-testid="maintenance-notify-error">{error}</p>}
        </form>
    );
}

// Garde-barrière : bloque les pages publiques quand la maintenance est active.
// Les routes /auth/* restent accessibles et les utilisateurs connectés ne sont jamais bloqués.
export function MaintenanceGate({ children }) {
    const { session, loading } = useAuth();
    const location = useLocation();
    const [status, setStatus] = useState(null);

    useEffect(() => {
        axios.get(`${API}/tech/maintenance`).then(r => setStatus(r.data)).catch(() => setStatus({ active: false }));
    }, []);

    const isAuthRoute = location.pathname.startsWith('/auth');
    const isAppRoute = location.pathname.startsWith('/app');
    const isContactRoute = location.pathname === '/p/contact';
    if (status?.active && !loading && !session && !isAuthRoute && !isAppRoute && !isContactRoute) {
        return <Maintenance message={status.message} />;
    }
    return children;
}
